import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type WorkspaceAgentComposerAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentComposerPills';
import { useWorkspaceAgentClipsForClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentClipsForClipIds';
import { usePrivateWorkspaceAgentPanelComposerPills } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import {
  getWorkspaceAgentGrayscaleSelection,
  type WorkspaceAgentGrayscaleSubject,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGrayscale';
import { selectedClipItemsSelector } from '~/store/selectedItems/selectors';

const toGrayscaleSubject = (
  clip: Pick<WorkspaceAgentGrayscaleSubject, 'ext' | 'thumbnailUrl' | 'type'>,
): WorkspaceAgentGrayscaleSubject => ({
  ext: clip.ext,
  thumbnailUrl: clip.thumbnailUrl,
  type: clip.type,
});

export const usePrivateWorkspaceAgentGrayscaleSelection = () => {
  const { scopePills } = usePrivateWorkspaceAgentPanelComposerPills();
  const selectedClipItems = useSelector(selectedClipItemsSelector);

  const assetPills = useMemo(
    () => scopePills.filter((pill): pill is WorkspaceAgentComposerAssetPill => pill.type === 'asset'),
    [scopePills],
  );
  const assetClipIds = useMemo(() => assetPills.map((pill) => pill.versionId), [assetPills]);
  const scopeClips = useWorkspaceAgentClipsForClipIds(assetClipIds);

  return useMemo(() => {
    if (selectedClipItems.length > 0) {
      return getWorkspaceAgentGrayscaleSelection(
        selectedClipItems.map((selected) =>
          toGrayscaleSubject({
            ext: selected.item.ext,
            thumbnailUrl: selected.item.assets?.image || null,
            type: selected.item.type,
          }),
        ),
      );
    }

    const clipsById = new Map(scopeClips.map((clip) => [clip.id, clip]));

    return getWorkspaceAgentGrayscaleSelection(
      assetPills.flatMap((pill) => {
        const clip = clipsById.get(pill.versionId);

        if (!clip) {
          return [];
        }

        return [
          toGrayscaleSubject({
            ext: clip.ext,
            thumbnailUrl: clip.assets?.image || pill.thumbnailUrl,
            type: clip.type,
          }),
        ];
      }),
    );
  }, [assetPills, scopeClips, selectedClipItems]);
};
